import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

const schema = z.object({
  telefono: z.string().min(6, "Teléfono inválido"),
});
type FormValues = z.infer<typeof schema>;

// Se monta una sola vez en Layout: mientras user.needsProfileCompletion sea true, tapa el panel
// entero con un modal sin botón de cerrar (onOpenChange no hace nada) — el backend además
// bloquea cualquier POST/PUT/DELETE de verdad, esto es solo la parte visible de ese bloqueo.
export function CompleteProfileModal() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (!user?.needsProfileCompletion) return null;

  async function onSubmit(data: FormValues) {
    try {
      await api.put("/auth/complete-profile", data);
      if (user && accessToken) {
        setSession(accessToken, { ...user, telefono: data.telefono, needsProfileCompletion: false });
      }
      toast.success("Perfil completado");
      toast.warning("Por seguridad, te recomendamos cambiar la contraseña que se te asignó al crear la cuenta.", {
        duration: Infinity,
        action: { label: "Cambiar contraseña", onClick: () => navigate("/perfil") },
      });
    } catch {
      toast.error("No se pudo guardar el teléfono");
    }
  }

  return (
    <Modal open onOpenChange={() => {}} blocking title="Completa tu perfil" description="Un último paso antes de poder usar el panel.">
      <div className="flex flex-col gap-4">
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mr-1.5 inline h-4 w-4 align-text-bottom" />
          Tu cuenta se ha dado de alta a mano y todavía no tiene teléfono de contacto. Hasta que lo
          rellenes no podrás crear ni modificar nada en el panel.
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Input
            id="complete-profile-telefono"
            label="Teléfono de contacto"
            placeholder="600 000 000"
            error={errors.telefono?.message}
            {...register("telefono")}
          />
          <Button type="submit" disabled={isSubmitting} className="w-fit">
            Guardar y continuar
          </Button>
        </form>
      </div>
    </Modal>
  );
}
